<!-- Explicit styleClass: PrimeNG concatenates the raw input into the container class, so leaving it unset leaks a literal "undefined". -->
<!-- defaultFocus="none": the headless template has none of PrimeNG's default-template focus targets,
     so the cesConfirmDialogA11y directive on the card restores aria + moves focus into the dialog. -->
<p-confirmDialog #cd styleClass="ces-confirm-dialog" defaultFocus="none">
  <ng-template #headless let-message let-onAccept="onAccept" let-onReject="onReject">
    @if (message) {
      <div
        cesConfirmDialogA11y
        labelledBy="ces-confirm-title"
        describedBy="ces-confirm-desc"
        class="relative flex flex-col items-center p-(--space-6) bg-surface-0 rounded-(--radius-lg) shadow-lg w-[30rem] max-w-[calc(100vw-3rem)]">
        <button
          type="button"
          class="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded text-(color:--text-secondary) transition-colors hover:bg-(--surface-1) hover:text-(color:--p-text-color) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(color:--p-primary-color)"
          [attr.aria-label]="'common.buttons.close' | transloco"
          (click)="cd.close()">
          <span class="material-symbols">close</span>
        </button>
        @if (message.icon) {
          @let caution = message.icon === 'caution';
          <!-- Amber caution for an irreversible non-delete action;
               neutral grey for a benign confirmation. -->
          <div
            class="inline-flex h-16 w-16 items-center justify-center rounded-full mb-(--space-4)"
            [class.bg-amber-100]="caution"
            [class.bg-(--surface-1)]="!caution">
            <span
              class="material-symbols text-2xl"
              [class.text-amber-600]="caution"
              [class.text-(color:--text-secondary)]="!caution"
              >{{ caution ? 'warning' : 'info' }}</span
            >
          </div>
        } @else {
          <!-- Destructive delete: trash + red confirm. -->
          <div class="rounded-full bg-(--p-red-200) inline-flex justify-center items-center h-16 w-16 mb-(--space-4)">
            <span class="material-symbols text-2xl text-(color:--p-red-100)">delete</span>
          </div>
        }
        <span id="ces-confirm-title" class="font-semibold text-xl mb-(--space-2)">{{ message.header }}</span>
        <p id="ces-confirm-desc" class="text-center text-(--text-secondary) mb-(--space-6)" [innerHTML]="message.message"></p>
        <div
          class="flex w-full"
          [class.justify-between]="message.rejectVisible !== false"
          [class.justify-end]="message.rejectVisible === false">
          @if (message.rejectVisible !== false) {
            <ces-button-tertiary [label]="message.rejectLabel || ('common.buttons.cancel' | transloco)" (onClick)="onReject()" />
          }
          @if (message.icon) {
            <ces-button [label]="message.acceptLabel || ('common.buttons.confirm' | transloco)" (onClick)="onAccept()" />
          } @else {
            <ces-delete-button [label]="message.acceptLabel || ('common.buttons.delete' | transloco)" (onClick)="onAccept()" />
          }
        </div>
      </div>
    }
  </ng-template>
</p-confirmDialog>
<p-toast position="bottom-right" />
<ces-busy-overlay />
@if (isAuthenticated$ | async) {
  <div class="flex h-screen w-full flex-col bg-(--surface-0)">
    <ces-toolbar />

    <div class="flex flex-1 overflow-hidden">
      <router-outlet name="sidebar" />
      <main class="flex-1 overflow-auto p-(--space-8)">
        <router-outlet />
      </main>
    </div>
  </div>
  <div
    class="fixed opacity-90 bottom-0 right-0 mr-(--space-1) mb-(--space-1) bg-(--surface-1) p-(--padding-card) rounded-(--radius-lg) border border-(--border) w-64 scale-75 origin-bottom-right [background:radial-gradient(125%_125%_at_50%_10%,white_40%,var(--p-orange-300)_100%)]"
    [class.hidden]="!configService.getSignal(ConfigurationPanel.CONFIG_KEY)()">
    <configuration-panel />
  </div>
} @else if (isPublicPage()) {
  <!-- Error pages and sign-out stay reachable without a session, even after a refused sign-in. -->
  <main class="h-screen w-full overflow-auto bg-(--surface-0) p-(--space-8)">
    <router-outlet />
  </main>
} @else if (signInFailed()) {
  <div class="flex h-screen w-full flex-col items-center justify-center gap-(--space-4) bg-(--surface-0)" role="alert">
    <p class="text-(color:--text-secondary)">{{ 'common.errors.signInFailed' | transloco }}</p>
    <ces-button [label]="'common.buttons.retry' | transloco" (onClick)="retrySignIn()" />
  </div>
} @else {
  <!-- Shown until a token is in hand. Without it the screens behind would appear with no data. -->
  <div
    class="flex h-screen w-full flex-col items-center justify-center gap-(--space-4) bg-(--surface-0)"
    role="status"
    aria-live="polite">
    <p-progressSpinner [style]="{ width: '40px', height: '40px' }" strokeWidth="4" animationDuration="1s" />
    <p class="text-(color:--text-secondary)">{{ 'common.loading.signingIn' | transloco }}</p>
  </div>
}
